import type {
    PatientDetailDto,
    SeriesDto,
} from '~/backend/connector-next-api/connector-next-schemas';
import {
    isImageSopClass,
    type ScannedInstance,
    type ScanReject,
    type ScanResult,
} from '~/domain/dicom/importScan';
import type { IntlShape } from 'react-intl';
import { seriesKey } from '~/backend/connector-next-api/seriesKey';
import { flattenSeries } from '~/containers/patient-list/model';
import { identityKey } from './identityKey';
import type { ImportMessage } from './importMessage';

/** The pure layer between a header scan and the confirm step: what the files claim and what the import will do. */

/** The identity fields a drop can disagree about. PatientID cannot: the drop is partitioned by it. */
export type ImportIdentityField = 'name' | 'sex' | 'birthDate';

/** The identity the dropped files declare. Read-only everywhere: the file is authoritative. */
export type ImportIdentity = {
    dicomPatientId: string;
    dicomPatientName?: string;
    patientSex?: string;
    /** DICOM DA, `YYYYMMDD`, as read; formatted for display at the edge. */
    patientBirthDate?: string;
    /**
     * Fields whose value differs between this patient's files. The card shows one value per field,
     * so a disagreement has to be stated: unstated, two people under one mistyped PatientID read as
     * one patient and are confirmed into one record.
     */
    conflicting: readonly ImportIdentityField[];
};

/** One patient's slice of a drop; a multi-patient drop becomes several of these. */
export type PatientDrop = {
    identity: ImportIdentity;
    series: SeriesDto[];
    instances: readonly ScannedInstance[];
    instanceCount: number;
};

export type ImportScanSummary =
    | { status: 'no-dicom'; rejects: ScanReject[] }
    | { status: 'ready'; drops: PatientDrop[]; rejects: ScanReject[] };

/** Where the import will land, once the server has answered for the scanned patient id. */
export type ImportTarget =
    | { kind: 'new-patient' }
    | { kind: 'existing-patient'; patientId: string };

/** A patient's existence-check status; kept separate from ImportTarget, which is the resolved commit-time decision. */
export type Verdict =
    | { kind: 'checking' }
    | { kind: 'failed'; message: ImportMessage }
    | { kind: 'resolved'; target: ImportTarget };

/** What one patient's commit actually did, per the server's own per-patient result. */
export type ImportOutcome = {
    patientId: string;
    wasNewPatient: boolean;
    instancesImported: number;
    instancesAlreadyPresent: number;
};

/** A queue entry's own progress through the wizard, independent of which one is on screen. */
export type QueueEntryStatus =
    | { kind: 'waiting' }
    /** `seriesKeys` records exactly what was sent, so a revisited entry reflects the real import. */
    | {
          kind: 'imported';
          outcome: ImportOutcome;
          seriesKeys: ReadonlySet<string>;
      }
    | { kind: 'skipped' };

function groupBy<T>(
    items: readonly T[],
    keyOf: (item: T) => string
): Map<string, T[]> {
    const groups = new Map<string, T[]>();
    for (const item of items) {
        const bucket = groups.get(keyOf(item));
        if (bucket) {
            bucket.push(item);
        } else {
            groups.set(keyOf(item), [item]);
        }
    }
    return groups;
}

/**
 * The first non-empty value across the drop, and whether the drop disagreed about it. Slices
 * legitimately omit optional tags, so absent never conflicts with present; two different values do,
 * and that is two people sharing one PatientID rather than one person described unevenly.
 */
function agreedValue(
    instances: readonly ScannedInstance[],
    pick: (instance: ScannedInstance) => string | undefined
): { value: string | undefined; conflicting: boolean } {
    let value: string | undefined;
    let key: string | undefined;
    let conflicting = false;

    for (const instance of instances) {
        const candidate = pick(instance);
        if (!candidate) {
            continue;
        }
        if (value === undefined) {
            value = candidate;
            key = identityKey(candidate);
        } else if (identityKey(candidate) !== key) {
            conflicting = true;
        }
    }
    return { value, conflicting };
}

/** Projects one series' scanned instances into the SeriesDto the worklist renders; server-owned fields left undefined. */
function toIncomingSeries(instances: readonly ScannedInstance[]): SeriesDto {
    const first = instances[0];
    return {
        seriesInstanceUid: first.seriesInstanceUid,
        modality: first.modality ?? undefined,
        seriesDescription: first.seriesDescription ?? undefined,
        seriesDateTime: agreedValue(instances, (i) => i.seriesDateTime).value,
        frameOfReferenceUid: undefined,
        slices: instances.map((instance) => ({
            sopInstanceUid: instance.sopInstanceUid,
        })),
    };
}

/** Groups scanned instances into series, ordered by series UID so the tree is deterministic. */
export function incomingSeriesOf(
    instances: readonly ScannedInstance[]
): SeriesDto[] {
    const bySeries = groupBy(instances, (i) => i.seriesInstanceUid);

    return [...bySeries.entries()]
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([, group]) => toIncomingSeries(group));
}

/** The distinct patient ids a scan found, ordered so the message reads the same every time. */
export function scannedPatientIds(
    instances: readonly ScannedInstance[]
): string[] {
    return [...new Set(instances.map((i) => i.dicomPatientId))].sort();
}

function identityOf(
    dicomPatientId: string,
    instances: readonly ScannedInstance[]
): ImportIdentity {
    const name = agreedValue(instances, (i) => i.dicomPatientName);
    const sex = agreedValue(instances, (i) => i.patientSex);
    const birthDate = agreedValue(instances, (i) => i.patientBirthDate);

    // Fixed field order, so the same drop always reports the same disagreement.
    const conflicting: ImportIdentityField[] = [];
    if (name.conflicting) {
        conflicting.push('name');
    }
    if (sex.conflicting) {
        conflicting.push('sex');
    }
    if (birthDate.conflicting) {
        conflicting.push('birthDate');
    }

    return {
        dicomPatientId,
        dicomPatientName: name.value,
        patientSex: sex.value,
        patientBirthDate: birthDate.value,
        conflicting,
    };
}

/** Splits a scan into one drop per distinct patient id, in scannedPatientIds order. */
export function partitionByPatient(
    instances: readonly ScannedInstance[]
): PatientDrop[] {
    const byPatient = groupBy(instances, (i) => i.dicomPatientId);

    return scannedPatientIds(instances).map((dicomPatientId) => {
        const patientInstances = byPatient.get(dicomPatientId) ?? [];
        return {
            identity: identityOf(dicomPatientId, patientInstances),
            series: incomingSeriesOf(patientInstances),
            instances: patientInstances,
            instanceCount: patientInstances.length,
        };
    });
}

/** Reduces a scan to what the wizard shows: no importable files refuses the drop, else a queue per patient. */
export function summarizeScan(scan: ScanResult): ImportScanSummary {
    if (scan.instances.length === 0) {
        return { status: 'no-dicom', rejects: scan.rejects };
    }

    return {
        status: 'ready',
        drops: partitionByPatient(scan.instances),
        rejects: scan.rejects,
    };
}

/** Every series' seriesKey in a drop: the default "all selected" state. */
export function allSeriesKeys(
    series: readonly SeriesDto[]
): ReadonlySet<string> {
    const keys = new Set<string>();
    for (const s of series) {
        const key = seriesKey(s);
        if (key !== undefined) {
            keys.add(key);
        }
    }
    return keys;
}

/** The series a selection keeps, in original order (what the composition footer describes). */
export function selectedSeriesOf(
    series: readonly SeriesDto[],
    selectedKeys: ReadonlySet<string>
): SeriesDto[] {
    return series.filter((s) => {
        const key = seriesKey(s);
        return key !== undefined && selectedKeys.has(key);
    });
}

/** A series contributes all its instances or none, never part. */
function instancesOfSeries(
    drop: PatientDrop,
    series: readonly SeriesDto[]
): ScannedInstance[] {
    const uids = new Set(
        series
            .map((s) => s.seriesInstanceUid)
            .filter((uid): uid is string => !!uid)
    );
    return drop.instances.filter((i) => uids.has(i.seriesInstanceUid));
}

/** The scanned instances of the selected series. */
export function selectedInstances(
    drop: PatientDrop,
    selectedKeys: ReadonlySet<string>
): ScannedInstance[] {
    return instancesOfSeries(drop, selectedSeriesOf(drop.series, selectedKeys));
}

/** The composition-footer figures for a selection. */
export type ImportSelectionSummary = {
    series: SeriesDto[];
    studyCount: number;
};

/**
 * What a selection amounts to for the review footer. `studyCount` is derived from the selected
 * instances rather than the drop, so both figures narrow together as series are deselected.
 */
export function selectionSummary(
    drop: PatientDrop,
    selectedKeys: ReadonlySet<string>
): ImportSelectionSummary {
    const series = selectedSeriesOf(drop.series, selectedKeys);
    const studyCount = new Set(
        instancesOfSeries(drop, series)
            .map((i) => i.studyInstanceUid)
            .filter((uid): uid is string => !!uid)
    ).size;
    return { series, studyCount };
}

/** What a patient's record already holds, in the two key spaces the connector dedupes on: image slices per series, standalone kinds patient-wide. */
export type StoredInstances = {
    /** Downloaded slices per stored image series UID. */
    readonly imageSlicesBySeriesUid: ReadonlyMap<string, ReadonlySet<string>>;
    /** SOP Instance UIDs of the standalone kinds, patient-wide. */
    readonly singleInstanceSopUids: ReadonlySet<string>;
};

/** Only a slice the connector reports downloaded counts as present; an announced C-MOVE row may never land. */
const STORED_SLICE_STATUS = 'Downloaded';

/** Reads a patient record into the key spaces {@link alreadyStoredSeriesKeys} matches against. */
export function storedInstancesOf(
    detail: PatientDetailDto | undefined
): StoredInstances {
    const imageSlicesBySeriesUid = new Map<string, Set<string>>();
    const singleInstanceSopUids = new Set<string>();

    for (const series of flattenSeries(detail)) {
        const slices = series.slices ?? [];
        if (slices.length > 0 && series.seriesInstanceUid) {
            let stored = imageSlicesBySeriesUid.get(series.seriesInstanceUid);
            if (!stored) {
                stored = new Set<string>();
                imageSlicesBySeriesUid.set(series.seriesInstanceUid, stored);
            }
            for (const slice of slices) {
                if (
                    slice.sopInstanceUid &&
                    slice.status === STORED_SLICE_STATUS
                ) {
                    stored.add(slice.sopInstanceUid);
                }
            }
        }
        // A stored standalone dataset carries its own SOP UID and no slices; an image series
        // carries slices and a null one, so the two arms never both fire for one row.
        if (series.sopInstanceUid) {
            singleInstanceSopUids.add(series.sopInstanceUid);
        }
    }

    return { imageSlicesBySeriesUid, singleInstanceSopUids };
}

/** The seriesKeys a drop's every scanned instance is already stored under; marked only on a demonstrable full match, since a wrong mark silently drops data. */
export function alreadyStoredSeriesKeys(
    drop: PatientDrop,
    stored: StoredInstances
): ReadonlySet<string> {
    const instancesBySeriesUid = groupBy(
        drop.instances,
        (i) => i.seriesInstanceUid
    );

    const marked = new Set<string>();
    for (const series of drop.series) {
        const key = seriesKey(series);
        const uid = series.seriesInstanceUid;
        if (key === undefined || !uid) {
            continue;
        }
        const instances = instancesBySeriesUid.get(uid) ?? [];
        if (instances.length === 0) {
            continue;
        }

        const images = instances.filter((i) => isImageSopClass(i.sopClassUid));
        const storedSopUids =
            images.length === instances.length
                ? stored.imageSlicesBySeriesUid.get(uid)
                : images.length === 0
                  ? stored.singleInstanceSopUids
                  : // Mixed classes in one series: no single key answers for it.
                    undefined;
        if (storedSopUids === undefined) {
            continue;
        }

        if (instances.every((i) => storedSopUids.has(i.sopInstanceUid))) {
            marked.add(key);
        }
    }
    return marked;
}

/** One message per reason; each carries its own count, so the clause agrees with it. */
export const REJECT_REASON_ID: Record<ScanReject['reason'], string> = {
    unreadable: 'patientList.import.reject.unreadable',
    'not-dicom': 'patientList.import.reject.notDicom',
    'unsupported-sop-class': 'patientList.import.reject.unsupportedSopClass',
    'missing-patient-id': 'patientList.import.reject.missingPatientId',
    'missing-study-uid': 'patientList.import.reject.missingStudyUid',
    'missing-series-uid': 'patientList.import.reject.missingSeriesUid',
    'missing-sop-uid': 'patientList.import.reject.missingSopUid',
    'duplicate-sop-uid': 'patientList.import.reject.duplicateSopUid',
};

/** How many files each reason accounted for, ordered by the reason key so the line reads the same every time. */
function rejectCounts(
    rejects: readonly ScanReject[]
): { reason: ScanReject['reason']; count: number }[] {
    const byReason = new Map<ScanReject['reason'], number>();
    for (const reject of rejects) {
        byReason.set(reject.reason, (byReason.get(reject.reason) ?? 0) + 1);
    }
    // Sorted on the reason enum, never on the translated text: the order must not move with the locale.
    return [...byReason.entries()]
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([reason, count]) => ({ reason, count }));
}

/** One line summarising what was skipped, or undefined when nothing was. */
export function rejectSummary(
    intl: IntlShape,
    rejects: readonly ScanReject[]
): string | undefined {
    if (rejects.length === 0) {
        return undefined;
    }
    const parts = rejectCounts(rejects).map(({ reason, count }) =>
        intl.formatMessage({ id: REJECT_REASON_ID[reason] }, { count })
    );
    return intl.formatMessage(
        { id: 'patientList.import.reject.summary' },
        {
            total: rejects.length,
            parts: intl.formatList(parts, { type: 'conjunction' }),
        }
    );
}
